/**
 * InternOnboarding — Gamified onboarding for intern members.
 * Tracks 4 steps:
 *  1. Profil personnel rempli
 *  2. Carte étudiante soumise
 *  3. Adhésion validée
 *  4. Découverte de l'écosystème (messagerie, formations, copilote IA)
 */
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Sparkles, X, Trophy, ChevronRight, CheckCircle2, Circle,
  User, IdCard, CreditCard, BookOpen, MessageSquare, Rocket, ArrowRight,
} from 'lucide-react';
import { API } from '@/App';

const STORAGE_KEY = 'intern_onboarding_dismissed_v1';
const WELCOME_KEY = 'intern_onboarding_welcome_v1';

const InternOnboarding = ({ user }) => {
  const navigate = useNavigate();
  const [welcomeOpen, setWelcomeOpen] = useState(false);
  const [dismissed, setDismissed] = useState(localStorage.getItem(STORAGE_KEY) === '1');

  useEffect(() => {
    if (!localStorage.getItem(WELCOME_KEY)) {
      setWelcomeOpen(true);
      localStorage.setItem(WELCOME_KEY, '1');
    }
  }, []);

  const steps = useMemo(() => {
    if (!user) return [];
    const hasProfile = !!(user.first_name && user.last_name && user.phone);
    const hasCard = !!user.student_card_status;
    const hasAdhesion = user.subscription_status === 'active';
    const hasTour = !!localStorage.getItem('intern_tour_done_v1');

    return [
      { id: 'profile', label: 'Complétez votre profil', desc: 'Nom, téléphone et informations personnelles', icon: User, done: hasProfile, action: () => navigate('/dashboard/profile'), actionLabel: 'Mon profil' },
      { id: 'card', label: 'Soumettez votre carte étudiante', desc: "Téléversez votre carte d'étudiant pour validation", icon: IdCard, done: hasCard, action: () => navigate('/dashboard/student-card'), actionLabel: 'Ma carte' },
      { id: 'adhesion', label: 'Validez votre adhésion', desc: 'Finalisez votre inscription à la SFOALC', icon: CreditCard, done: hasAdhesion, action: () => navigate('/adhesion'), actionLabel: 'Adhérer' },
      { id: 'tour', label: "Découvrez l'écosystème", desc: 'Messagerie, formations, copilote IA et plus', icon: BookOpen, done: hasTour, action: () => {
        localStorage.setItem('intern_tour_done_v1', '1');
        navigate('/copilot');
      }, actionLabel: 'Explorer' },
    ];
  }, [user, navigate]);

  const completedCount = steps.filter(s => s.done).length;
  const progress = steps.length > 0 ? Math.round((completedCount / steps.length) * 100) : 0;

  if (dismissed || progress === 100) return null;

  return (
    <>
      {/* Welcome modal */}
      {welcomeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setWelcomeOpen(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 text-center" data-testid="intern-welcome-modal">
            <div className="w-16 h-16 mx-auto mb-4 rounded-2xl bg-gradient-to-br from-[#35AFE4] to-[#1e3a5f] flex items-center justify-center">
              <Rocket size={28} className="text-white" />
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">Bienvenue à la SFOALC !</h2>
            <p className="text-sm text-gray-500 mb-4">En tant qu'interne, vous accédez aux formations, au copilote IA et à la communauté SFOALC. Complétez ces quelques étapes pour profiter de tous les avantages.</p>
            <button onClick={() => setWelcomeOpen(false)}
              className="px-6 py-2.5 bg-[#35AFE4] text-white rounded-xl font-bold text-sm hover:bg-[#2a9cd4] transition-colors"
              data-testid="intern-welcome-start-btn">
              C'est parti <ArrowRight size={14} className="inline ml-1" />
            </button>
          </div>
        </div>
      )}

      {/* Progress widget */}
      <div className="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm" data-testid="intern-onboarding-widget">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#35AFE4]/10 flex items-center justify-center">
              <Trophy size={16} className="text-[#35AFE4]" />
            </div>
            <div>
              <p className="text-sm font-bold text-gray-900">Démarrage</p>
              <p className="text-xs text-gray-400">{completedCount}/{steps.length} étapes</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="text-sm font-bold text-[#35AFE4]">{progress}%</span>
            <button onClick={() => { setDismissed(true); localStorage.setItem(STORAGE_KEY, '1'); }}
              className="p-1 rounded-lg hover:bg-gray-100 text-gray-400" data-testid="dismiss-onboarding">
              <X size={14} />
            </button>
          </div>
        </div>

        {/* Progress bar */}
        <div className="w-full h-2 bg-gray-100 rounded-full mb-4 overflow-hidden">
          <div className="h-full bg-gradient-to-r from-[#35AFE4] to-[#059669] rounded-full transition-all duration-500" style={{ width: `${progress}%` }} />
        </div>

        {/* Steps */}
        <div className="space-y-2">
          {steps.map((step) => {
            const Icon = step.icon;
            return (
              <div key={step.id}
                className={`flex items-center gap-3 p-3 rounded-xl transition-colors ${step.done ? 'bg-emerald-50/50' : 'bg-gray-50 hover:bg-gray-100 cursor-pointer'}`}
                onClick={!step.done ? step.action : undefined}
                data-testid={`step-${step.id}`}>
                {step.done ? (
                  <CheckCircle2 size={20} className="text-emerald-500 flex-shrink-0" />
                ) : (
                  <Circle size={20} className="text-gray-300 flex-shrink-0" />
                )}
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${step.done ? 'text-emerald-700 line-through' : 'text-gray-900'}`}>{step.label}</p>
                  <p className="text-[10px] text-gray-400">{step.desc}</p>
                </div>
                {!step.done && (
                  <button onClick={(e) => { e.stopPropagation(); step.action(); }}
                    className="flex items-center gap-1 px-2.5 py-1.5 rounded-lg bg-[#35AFE4] text-white text-[10px] font-bold hover:bg-[#2a9cd4] transition-colors flex-shrink-0">
                    {step.actionLabel} <ChevronRight size={10} />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </>
  );
};

export default InternOnboarding;
